import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAdminToken, useAdminGuard } from './AdminLayout'
import { useAdminStore } from '../../store/adminStore'
import { Badge, type BadgeTone } from '../../components/Badge'

const ISOFT_ROLES = ['super_admin', 'support', 'sales'] as const
type IsoftRole = typeof ISOFT_ROLES[number]

const ROLE_TONE: Record<string, BadgeTone> = {
  super_admin: 'info',
  support:     'teal',
  sales:       'warning',
}

interface IsoftUser {
  id: string
  email: string
  full_name: string | null
  picture: string | null
  roles: string[]
  created_at: string
  last_login: string | null
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// ── Add user modal ────────────────────────────────────────────────────────────

function AddUserModal({ onClose }: { onClose: () => void }) {
  const token = useAdminToken()
  const qc    = useQueryClient()
  const [email, setEmail]   = useState('')
  const [roles, setRoles]   = useState<IsoftRole[]>(['support'])
  const [err, setErr]       = useState('')

  const toggleRole = (r: IsoftRole) =>
    setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r])

  const mutation = useMutation({
    mutationFn: () =>
      fetch('/api/v1/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, roles }),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).detail ?? 'Greška')
        return r.json()
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['isoft-users'] }); onClose() },
    onError: (e: unknown) => setErr((e as Error).message),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900">Dodati I-SOFT korisnika</h2>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Email adresa <span className="text-red-600">*</span></label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="ime.prezime@isoft.rs"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-hidden"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Role</label>
          <div className="flex flex-col gap-2">
            {ISOFT_ROLES.map((r) => (
              <label key={r} className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(r)}
                  className="rounded-sm text-brand-cyan focus:ring-blue-500 border-gray-300" />
                <Badge tone={ROLE_TONE[r]}>{r}</Badge>
              </label>
            ))}
          </div>
        </div>

        {err && <p className="text-xs text-red-600">{err}</p>}

        <div className="flex gap-2 pt-2">
          <button
            onClick={() => { setErr(''); mutation.mutate() }}
            disabled={mutation.isPending || !email || roles.length === 0}
            className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium py-2.5 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Dodavanje…' : 'Dodati'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminUsers() {
  const token        = useAdminToken()
  const { user }     = useAdminStore()
  const qc           = useQueryClient()
  const handleUnauth = useAdminGuard()
  const isSuperAdmin = user?.isoftRoles.includes('super_admin') ?? false

  const [adding,   setAdding]   = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editRoles, setEditRoles] = useState<IsoftRole[]>([])

  const { data: users = [], isLoading, isError } = useQuery<IsoftUser[]>({
    queryKey: ['isoft-users', token],
    queryFn: async () => {
      const r = await fetch('/api/v1/admin/users', { headers: { Authorization: `Bearer ${token}` } })
      handleUnauth(r)
      if (!r.ok) throw new Error((await r.json()).detail ?? 'Greška pri učitavanju')
      return r.json()
    },
    staleTime: 30_000,
  })

  const updateRoles = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) =>
      fetch(`/api/v1/admin/users/${id}/roles`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ roles }),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).detail ?? 'Greška')
        return r.json()
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['isoft-users'] }); setEditingId(null) },
  })

  const removeUser = useMutation({
    mutationFn: (id: string) =>
      fetch(`/api/v1/admin/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['isoft-users'] }),
  })

  const startEdit = (u: IsoftUser) => {
    setEditingId(u.id)
    setEditRoles(u.roles as IsoftRole[])
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">I-SOFT tim</h1>
          <p className="text-sm text-gray-500 mt-0.5">Korisnici admin panela — Google prijava</p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-2 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium px-4 py-2 rounded-lg"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Dodati korisnika
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
        </div>
      ) : isError ? (
        <div className="py-16 text-center">
          <p className="text-sm text-red-600">Greška pri učitavanju korisnika.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {users.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm text-gray-500">Nema korisnika. Dodajte prvog.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Korisnik', 'Role', 'Poslednja prijava', 'Dodat', ...(isSuperAdmin ? ['Akcije'] : [])].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture
                          ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                          : <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-brand-cyan-text">
                              {(u.full_name ?? u.email)[0].toUpperCase()}
                            </div>
                        }
                        <div>
                          <p className="font-medium text-gray-900">{u.full_name ?? '—'}</p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {editingId === u.id ? (
                        <div className="flex flex-col gap-1">
                          {ISOFT_ROLES.map((r) => (
                            <label key={r} className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={editRoles.includes(r)}
                                onChange={() => setEditRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r])}
                                className="rounded-sm text-brand-cyan border-gray-300" />
                              <span className="text-xs text-gray-600">{r}</span>
                            </label>
                          ))}
                          <div className="flex gap-1 mt-1">
                            <button
                              onClick={() => updateRoles.mutate({ id: u.id, roles: editRoles })}
                              disabled={updateRoles.isPending || editRoles.length === 0}
                              className="text-xs text-brand-cyan-text hover:text-brand-midnight font-medium disabled:opacity-50"
                            >
                              {updateRoles.isPending ? 'Čuvanje…' : 'Sačuvati'}
                            </button>
                            <span className="text-gray-300">·</span>
                            <button onClick={() => setEditingId(null)} className="text-xs text-gray-500 hover:text-gray-700">
                              Otkazati
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {u.roles.map((r) => (
                            <Badge key={r} tone={ROLE_TONE[r] ?? 'neutral'}>{r}</Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(u.last_login)}</td>
                    <td className="px-5 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(u.created_at)}</td>
                    {isSuperAdmin && (
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          {editingId !== u.id && (
                            <button onClick={() => startEdit(u)}
                              className="text-xs font-medium text-gray-600 hover:text-brand-cyan-text hover:bg-blue-50 px-2 py-1.5 rounded-lg transition-colors">
                              Izmeni role
                            </button>
                          )}
                          {u.id !== user?.id && (
                            <button
                              onClick={() => { if (confirm(`Ukloniti ${u.email}?`)) removeUser.mutate(u.id) }}
                              className="text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1.5 rounded-lg transition-colors"
                            >
                              Ukloniti
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!isSuperAdmin && (
        <p className="text-xs text-gray-400 text-center">
          Samo super_admin može upravljati korisnicima.
        </p>
      )}

      {adding && <AddUserModal onClose={() => setAdding(false)} />}
    </div>
  )
}
